/**
 * O desenho do diagrama, os componentes dele e a edição que não precisa do traço.
 *
 * **O que aparece aqui é o desenho GRAVADO, não um desenho novo.** A API ainda
 * não chama o `Desenhar()` — o motor existe em net8.0 na `BibliotecaCore` e é o
 * mesmo que serve o diagrama no site, mas chamá-lo daqui depende de uma decisão
 * de dependência. Enquanto isso: o `Desenhar()` começa devolvendo a coluna `svg`
 * quando ela existe e o diagrama não está marcado para atualizar, então **o que
 * está gravado é o que o site do cliente serve**. Mostrar essa coluna não é
 * maquete, é o desenho publicado — de 3.815 dos 4.829 diagramas.
 *
 * E a API sabe dizer quando ele está velho: as 13 escritas da edição fina marcam
 * `atualizar = 1`, e `desenho/estado` devolve isso. Então a tela mostra o traço
 * **com a data e o aviso**, em vez de mostrar um desenho antigo calado.
 *
 * **A edição que entra é a que a lista resolve.** Cor, desenho interno, texto do
 * terminal, a ficha do componente, a lixeira e o agrupamento. A geometria (altura,
 * deslocar, ramificação, junção) também grava, pelo editor do componente, mas
 * **não redesenha o diagrama**: ela marca `atualizar = 1` e o traço novo só
 * aparece quando se aperta Redesenhar. A inclusão de ligações em
 * lote saiu daqui: ela não depende do traço, e mora em `ligacoesEmLote.ts`.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { api, noProjeto, obter } from "./cliente"

// ------------------------------------------------------------------ o traço

export type EstadoDoDesenho = {
  /** Há SVG gravado. 3.815 dos 4.829 diagramas têm. */
  tem: boolean
  /** Houve edição depois do último desenho: o que está gravado é anterior a ela. */
  desatualizado: boolean
  data: string | null
  tamanho: number
}

/** Barato — só o tamanho e as marcas, sem o SVG. */
export function usarEstadoDoDesenho(diagramaId: number) {
  return useQuery({
    queryKey: ["diagrama-desenho-estado", diagramaId] as const,
    enabled: diagramaId > 0,
    staleTime: 15_000,
    queryFn: () => obter<EstadoDoDesenho>(`api/diagramas/${diagramaId}/desenho/estado`, {}),
  })
}

/**
 * Baixa o SVG e devolve uma URL de blob para pôr num `<img>`.
 *
 * **Blob e `<img>`, não `innerHTML`.** O SVG vem do banco e é grande (89 kB em
 * média, 523 kB no maior); injetá-lo no DOM abriria a porta para o que estiver
 * dentro dele executar. Dentro de um `<img>`, um SVG não roda script — e a
 * imagem ainda escala, que é o que se quer de um desenho.
 *
 * Quem chama é responsável por `URL.revokeObjectURL` quando trocar de diagrama.
 *
 * O par disto é `baixarPreviaDoDesenho`: **este** devolve o que está gravado, **aquele**
 * devolve o que o motor daria hoje. Lado a lado é o que mostra o que a edição fez.
 */
export async function baixarDesenho(diagramaId: number): Promise<string> {
  const r = await api.get(`api/diagramas/${diagramaId}/desenho`, { responseType: "blob" })
  return URL.createObjectURL(new Blob([r.data], { type: "image/svg+xml" }))
}

// ------------------------------------------------------------------ redesenhar

/** Em que classe cai a diferença entre o desenho gravado e o que o motor daria hoje. */
export type DesfechoDoRedesenho = "Igual" | "UltimoDigito" | "Numero" | "Estrutura"

export type VereditoDoRedesenho = {
  desfecho: DesfechoDoRedesenho
  bytesGravado: number
  bytesGerado: number
  explicacao: string
  /** O primeiro ponto que divergiu, quando há um. */
  pista: string
}

export type RedesenhoDoDiagrama = {
  diagramaId: number
  versao: number
  estavaMarcado: boolean
  gravou: boolean
  milissegundos: number
  veredito: VereditoDoRedesenho
}

/**
 * **O que o redesenho daria, sem gravar.**
 *
 * O motor é o `Desenhar()` da `BibliotecaCore` — o mesmo que serve o diagrama no
 * site — e esta chamada monta o diagrama inteiro a partir do banco: de 1 a 60
 * segundos conforme o tamanho. É botão, não consulta de fundo, e por isso não
 * tem `useQuery`: roda quando se pede.
 *
 * A trava do canário deixa passar (está em `CONFERENCIAS`): a conferência é
 * justamente o que mostra o estrago antes de haver estrago.
 */
export async function baixarPreviaDoDesenho(diagramaId: number): Promise<string> {
  const r = await api.get(`api/diagramas/${diagramaId}/desenhar/previa`,
    { responseType: "blob", timeout: 120_000 })
  return URL.createObjectURL(new Blob([r.data], { type: "image/svg+xml" }))
}

export function usarConferirRedesenho(diagramaId: number) {
  return useMutation({
    mutationFn: async () => {
      const r = await api.post<RedesenhoDoDiagrama>(
        `api/diagramas/${diagramaId}/desenhar/conferir`, undefined, { timeout: 120_000 })
      return r.data
    },
  })
}

/**
 * **Redesenha e grava.** Escreve a coluna `svg` e baixa o `atualizar` — é o
 * `AtualizarSVG()` do Sistema antigo, e muda o que o site do cliente serve.
 */
export function usarRedesenhar(projetoId: number, diagramaId: number) {
  const invalidar = usarInvalidar(projetoId, diagramaId)
  return useMutation({
    mutationFn: async () => {
      const r = await api.post<RedesenhoDoDiagrama>(
        `api/diagramas/${diagramaId}/desenhar`, undefined,
        noProjeto(projetoId, { timeout: 120_000 }))
      return r.data
    },
    onSuccess: invalidar,
  })
}

// ------------------------------------------------------------------ componentes

export type ComponenteDoDiagrama = {
  id: number
  pinoutId: number
  componenteId: number
  codigo: string
  descricao: string
  posicao: string
  ordem: number
  coluna: number
  titulo: string
  tipo: number
  texto: string
  altura: number
  ocultar: boolean
  excluido: boolean
  circuitoTipo: number
  circuitoApelido: string
  cor: string
  png: string
  desenhoInternoId: number
  terminais: number
  /** Não aparece em tela; vem para o formulário poder devolvê-lo intacto. */
  marginBottom: number
  /** Idem. */
  tamanhoTextoPino: number
}

/**
 * Os componentes do diagrama.
 *
 * Era o que faltava para o bloco existir: a API tinha **seis** rotas que editam
 * componente de diagrama e **nenhuma** que os listasse — não havia em que clicar.
 */
export function usarComponentesDoDiagrama(diagramaId: number) {
  return useQuery({
    queryKey: ["diagrama-componentes", diagramaId] as const,
    enabled: diagramaId > 0,
    staleTime: 10_000,
    queryFn: () => obter<ComponenteDoDiagrama[]>(`api/diagramas/${diagramaId}/componentes`, {}),
  })
}

// ------------------------------------------------------------------ verificação

export type VerificacaoDoDiagrama = {
  limpo: boolean
  [k: string]: unknown
}

/**
 * As três conferências do diagrama, **só leitura**.
 *
 * No sistema antigo a lista de não visualizados vem com um botão que os exclui
 * em massa; aqui a verificação só informa — lista de conferência com
 * apagar-tudo ao lado é acidente esperando acontecer.
 */
export function usarVerificacaoDoDiagrama(diagramaId: number, ligado: boolean) {
  return useQuery({
    queryKey: ["diagrama-verificacao", diagramaId] as const,
    enabled: ligado && diagramaId > 0,
    staleTime: 10_000,
    queryFn: () => obter<VerificacaoDoDiagrama>(`api/diagramas/${diagramaId}/verificacao`, {}),
  })
}

// ------------------------------------------------------------------ edição

function usarInvalidar(projetoId: number, diagramaId: number) {
  const qc = useQueryClient()
  return () => {
    qc.invalidateQueries({ queryKey: ["diagrama-componentes", diagramaId] })
    qc.invalidateQueries({ queryKey: ["diagrama-desenho-estado", diagramaId] })
    qc.invalidateQueries({ queryKey: ["diagrama-excluidos", diagramaId] })
    qc.invalidateQueries({ queryKey: ["diagrama-verificacao", diagramaId] })
    qc.invalidateQueries({ queryKey: ["diagramas", projetoId] })
  }
}

/**
 * A ficha inteira do componente.
 *
 * **`PUT componentes/{id}` é substituição completa**: ele grava título, texto,
 * tipo, altura, ocultar, `tamanhoTextoPino` e `marginBottom`, mais a cor e o
 * desenho interno da config. Mandar meia ficha zera a outra metade — por isso os
 * dois campos que nenhuma tela mostra vêm na listagem e voltam daqui intactos.
 *
 * `deslocar` é comando, não estado: só faz efeito quando há desenho interno, e
 * aí marca `deslocar` em todas as ligações do componente. `false` não desmarca
 * nada, então o padrão é seguro.
 */
export type FichaDoComponente = {
  titulo: string
  tipo: number
  texto: string
  /** No corpo o campo se chama `componenteCor` — é a cor da config, não a do diagrama. */
  componenteCor: string
  altura: number
  ocultar: boolean
  marginBottom: number
  tamanhoTextoPino: number
  diagramaDesenhoInternoId: number
  deslocar: boolean
}

/** A cor de função do módulo de controle. */
export const COR_DE_MODULO = "#FFE3A7"

/**
 * Troca a cor da ficha. A cor de módulo de controle já traz o desenho interno
 * de módulo — o 63 para quem está à direita do principal, o 4 à esquerda —, como
 * o `Diagrama.Componente.Incluir.js` do sistema antigo fazia ao escolher a cor.
 * Dá para trocar o desenho depois; outra cor não mexe nele.
 */
export function comCor(ficha: FichaDoComponente, cor: string, posicao: string | undefined): FichaDoComponente {
  if (cor.trim().toUpperCase() !== COR_DE_MODULO) return { ...ficha, componenteCor: cor }
  return { ...ficha, componenteCor: cor, diagramaDesenhoInternoId: posicao === "left" ? 4 : 63 }
}

/** Monta a ficha a partir da linha da listagem, para nada se perder no caminho. */
export function fichaDe(c: ComponenteDoDiagrama): FichaDoComponente {
  return {
    titulo: c.titulo,
    tipo: c.tipo,
    texto: c.texto,
    componenteCor: c.cor,
    altura: c.altura,
    ocultar: c.ocultar,
    marginBottom: c.marginBottom,
    tamanhoTextoPino: c.tamanhoTextoPino,
    diagramaDesenhoInternoId: c.desenhoInternoId,
    deslocar: false,
  }
}

export function usarEditarComponenteDoDiagrama(projetoId: number, diagramaId: number) {
  const invalidar = usarInvalidar(projetoId, diagramaId)
  return useMutation({
    mutationFn: async ({ id, ficha }: { id: number; ficha: FichaDoComponente }) => {
      await api.put(`api/diagramas/componentes/${id}`, ficha, noProjeto(projetoId))
    },
    onSuccess: invalidar,
  })
}

/** Uma cor por componente, em lote — é a tela de cores do legado. */
export function usarEditarCoresEmLote(projetoId: number, diagramaId: number) {
  const invalidar = usarInvalidar(projetoId, diagramaId)
  return useMutation({
    mutationFn: async (componentes: { diagramaComponenteId: number; cor: string }[]) => {
      const r = await api.put<{ alterados: number }>(
        "api/diagramas/componentes/cores", { componentes }, noProjeto(projetoId))
      return r.data
    },
    onSuccess: invalidar,
  })
}

export function usarEditarDesenhoInternoEmLote(projetoId: number, diagramaId: number) {
  const invalidar = usarInvalidar(projetoId, diagramaId)
  return useMutation({
    mutationFn: async (
      componentes: { diagramaComponenteId: number; diagramaDesenhoInternoId: number; deslocar: boolean }[],
    ) => {
      const r = await api.put<{ alterados: number }>(
        "api/diagramas/componentes/desenho-interno", { componentes }, noProjeto(projetoId))
      return r.data
    },
    onSuccess: invalidar,
  })
}

/** Só o texto de cada terminal — o resto do terminal é geometria e fica de fora. */
export function usarEditarTextoDeTerminais(projetoId: number, diagramaId: number) {
  const invalidar = usarInvalidar(projetoId, diagramaId)
  return useMutation({
    mutationFn: async (terminais: { diagramaLigacaoId: number; texto: string }[]) => {
      const r = await api.put<{ alterados: number }>(
        "api/diagramas/terminais/textos", { terminais }, noProjeto(projetoId))
      return r.data
    },
    onSuccess: invalidar,
  })
}

/** RN-15: marca como excluído. Não apaga linha — a lixeira restaura. */
export function usarExcluirDoDiagrama(projetoId: number, diagramaId: number) {
  const invalidar = usarInvalidar(projetoId, diagramaId)
  return useMutation({
    mutationFn: async ({ tipo, id }: { tipo: "componentes" | "ligacoes"; id: number }) => {
      await api.post(`api/diagramas/${tipo}/${id}/excluir`, undefined, noProjeto(projetoId))
    },
    onSuccess: invalidar,
  })
}

/** Limpa o PNG de fundo. Subir imagem nova espera o bloco do servidor de arquivos. */
export function usarRemoverPngDoComponente(projetoId: number, diagramaId: number) {
  const invalidar = usarInvalidar(projetoId, diagramaId)
  return useMutation({
    mutationFn: async (diagramaComponenteId: number) => {
      await api.delete(`api/diagramas/componentes/${diagramaComponenteId}/png`, noProjeto(projetoId))
    },
    onSuccess: invalidar,
  })
}

// ------------------------------------------------------------------ agrupar

export type ComponenteQueSome = {
  id: number
  nome: string
  ligacoesQueMudam: number
  ligacoesNaLixeira: number
  apontamParaEle: number
}

export type ConferenciaDeAgrupamento = {
  principalId: number
  principal: string
  somem: ComponenteQueSome[]
  totalQueMuda: number
  totalNaLixeira: number
  avisos: string[]
}

/**
 * O que o agrupamento faria, sem fazer.
 *
 * `selecionados` vai **cru**, com vírgulas, porque a regra do legado é sobre a
 * string: sem vírgula ele não funde nada, e a conferência tem de conferir a
 * mesma coisa que a operação.
 */
export function usarConferirAgrupamento(
  principalId: number, selecionados: string, ligado: boolean,
) {
  return useQuery({
    queryKey: ["agrupar-conferir", principalId, selecionados] as const,
    enabled: ligado && principalId > 0,
    staleTime: 5_000,
    queryFn: () => obter<ConferenciaDeAgrupamento>("api/diagramas/agrupar/conferir", {
      diagramaComponenteId: principalId, selecionados,
    }),
  })
}

/** RN-14. **DELETE de verdade** — não é a lixeira. */
export function usarAgrupar(projetoId: number, diagramaId: number) {
  const invalidar = usarInvalidar(projetoId, diagramaId)
  return useMutation({
    mutationFn: async ({ principalId, selecionados }: { principalId: number; selecionados: string }) => {
      // Os parâmetros vão na URL e não em `params`: com corpo `undefined` o axios
      // infere o genérico do corpo e briga com o config. A trava enxerga o projeto
      // pelo cabeçalho do `noProjeto`, que é o que ela precisa.
      const q = `diagramaComponenteId=${principalId}&selecionados=${encodeURIComponent(selecionados)}`
      await api.post(`api/diagramas/agrupar?${q}`, undefined, noProjeto(projetoId))
    },
    onSuccess: invalidar,
  })
}
